import React, { useState } from 'react';
import { Button, Divider } from 'semantic-ui-react';
import * as Yup from 'yup';

import { Typography } from 'src/components/coreUI/index';
import { formatPrice } from 'src/utils/helper';
import { createPaymentIntent } from 'src/thunks/payment';
import { useAppDispatch, useAppSelector } from 'src/store';
import dayjs from 'src/libs/dayjs';
import { FREE_PRICE, FULL_MONTH } from 'src/constants/app';
import Form from 'src/components/form';
import { useForm } from 'src/hooks';
import {
  validateCouponCode,
  ValidateCouponCode,
  ValidCoupon,
} from 'src/thunks/coupon';

type Props = {
  course?: Model.Class;
};

const TierPricingForm: React.FC<Props> = ({ course }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const user_id = useAppSelector((state) => state.ui.app.me?.id);
  const dispatch = useAppDispatch();
  const validPrices = (course?.prices || []).filter(
    (p) => ![FREE_PRICE].includes(p.price) && !p.is_extension,
  );
  const [price, setPrice] = useState<Model.Price | undefined>(
    course?.prices?.[0],
  );

  React.useEffect(() => {
    setPrice(validPrices[0]);
    setValidCoupon(undefined); // Reset coupon when price changes
  }, [course]);

  const [validCoupon, setValidCoupon] = useState<ValidCoupon>();

  const isFree = price?.price === FREE_PRICE;

  return (
    <div className="p-12">
      <h3>Select package</h3>

      <div className="radio pricing">
        {(course?.prices || [])
          // TODO: will remove after beta, FREE_PRICE is for beta tester
          .filter((p) => ![FREE_PRICE].includes(p.price) && !p.is_extension)
          .map((p) => {
            return (
              <div
                key={p.id}
                className={`item ${p.id === price?.id ? 'active' : ''}`}
                onClick={() => {
                  setPrice(p);
                  setValidCoupon(undefined);
                }}
              >
                <div className="price">{formatPrice(p.price)}</div>
                <div>{p.limit_months} MONTHS ACCESS</div>
              </div>
            );
          })}
      </div>

      <div className="text-gray mt-24">
        Course will be available for {price?.limit_months} months, until{' '}
        {dayjs().add(price?.limit_months!, 'month').format(FULL_MONTH)}
      </div>

      {!isFree && (
        <CouponForm
          validCoupon={validCoupon}
          price={price}
          onSuccess={setValidCoupon}
        />
      )}

      <Divider />
      <Typography.Paragraph className="d-flex-between">
        Total (1 item){' '}
        <span className="fs-24 fw-bold text-primary">
          {!validCoupon && formatPrice(price?.price)}

          {!!validCoupon &&
            price?.price &&
            formatPrice(price.price - validCoupon.discount_amount)}
        </span>
      </Typography.Paragraph>

      <Button
        primary
        fluid
        loading={loading}
        disabled={isFree}
        onClick={async () => {
          setLoading(true);
          await dispatch(
            createPaymentIntent({
              user_id,
              class_id: course?.id,
              class_price_id: price?.id,
              coupon: validCoupon?.id,
            }),
          );
        }}
      >
        Next
      </Button>
    </div>
  );
};

type CouponFormProps = {
  price?: Model.Price;
  onSuccess?: (value: ValidCoupon | undefined) => void;
  validCoupon?: ValidCoupon;
};

const CouponForm: React.FC<CouponFormProps> = ({
  price,
  validCoupon,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { getFieldInfo, submitForm, isSubmitting, values } =
    useForm<ValidateCouponCode>({
      initialValues: {
        class_price_id: price?.id,
        coupon_code: '',
      },
      async onSubmit(value) {
        const res = await dispatch(validateCouponCode(value));

        if (validateCouponCode.fulfilled.match(res)) {
          const coupon = res.payload as unknown as ValidCoupon;
          onSuccess?.(coupon);
        }
      },
      enableReinitialize: true,
      validationSchema: Yup.object().shape({
        coupon_code: Yup.string().max(255).label('Coupon'),
      }),
    });

  return (
    <React.Fragment>
      <Divider />
      <Form>
        <div className="d-flex-between my-12">
          <span className="fs-16">Coupon (1 item)</span>
          <span className="fs-18 fw-bold text-primary">
            {validCoupon && validCoupon.amount_off
              ? formatPrice(validCoupon.discount_amount)
              : null}
            {validCoupon && validCoupon.percent_off
              ? `${validCoupon.percent_off}%`
              : null}
          </span>
        </div>

        <Form.Group unstackable className="coupon">
          <Form.Input
            disabled={!!validCoupon}
            placeholder="Coupon code"
            className="w-100"
            {...getFieldInfo('coupon_code')}
          />

          {validCoupon && (
            <span className="clear" onClick={() => onSuccess?.(undefined)}>
              &#x2715;
            </span>
          )}

          {validCoupon ? (
            <div className="applied">Applied!</div>
          ) : (
            <Button
              basic
              color="purple"
              className="mr-8"
              disabled={!values.coupon_code}
              loading={isSubmitting}
              onClick={submitForm}
            >
              Apply
            </Button>
          )}
        </Form.Group>
      </Form>
    </React.Fragment>
  );
};

export default TierPricingForm;
